import { isNil, last } from "lodash";
import { Row } from "PFApp/booking/types";
import React, { forwardRef, JSX, RefObject, useMemo } from "react";

import { WorkforceCalendarData } from "../../../context/booking_overview_context.types";
import { useBookingRowsContext } from "../../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../../context/bookings_overview_context";
import Misalignments from "../../bars/misalignments";
import { Overbookings } from "../../bars/overbookings/overbookings";
import { BAR_HEIGHT, OVERBOOKINGS_TOP, ROW_PADDING, VERTICAL_BARS_MARGIN } from "../../calendar";
import { BasicRow } from "../basic_row";
import { HoveredPeriodIndicator } from "../hovered_period_indicator";
import { HoverPeriodData } from "../row";
import {
  checkShouldDisplayMisalignments,
  checkShouldDisplayOverbookings,
  getRowActivityId,
  getRowProfileId,
  useShouldHoverPeriod
} from "./bookings_row.utils";

type BookingsRowProps = {
  row: Row;
  startDate: string;
  endDate: string;
  draggedBookingId: number | null;
  shouldBarsOverlap: boolean;
  onLoadingChange: (value: boolean) => void;
  onBarDragStart: (value: number) => void;
  onBarDragEnd: () => void;
  hoveredPeriodData: HoverPeriodData | null;
};

export const BookingsRow = forwardRef<HTMLDivElement, BookingsRowProps>(
  (
    {
      row,
      startDate,
      endDate,
      onLoadingChange,
      draggedBookingId,
      onBarDragStart,
      onBarDragEnd,
      shouldBarsOverlap,
      hoveredPeriodData
    },
    rowRef
  ): JSX.Element => {
    const { calendarMode } = useBookingsOverviewContext();
    const {
      calendarData: { calendarGranularity }
    } = useBookingsOverviewContext<WorkforceCalendarData>();
    const { warningsByProfile, rowsByCalendarMode, rowDimensionsByCalendarMode, barsByRowKey } =
      useBookingRowsContext();

    const rows = rowsByCalendarMode[calendarMode];
    const parentRow = rows.find(({ key }) => last(row.parents) === key);
    const profileId = getRowProfileId(calendarMode, row, parentRow);
    const activityId = getRowActivityId(calendarMode, row, parentRow);
    const dimensions = rowDimensionsByCalendarMode[calendarMode][row.key];
    const barRowsCount = (barsByRowKey[row.key] || []).length;

    const shouldDisplayOverbookings = checkShouldDisplayOverbookings(row, calendarMode, warningsByProfile);
    const shouldDisplayMisalignments = checkShouldDisplayMisalignments(row, calendarMode);
    const shouldHoverPeriod = useShouldHoverPeriod(row, draggedBookingId, hoveredPeriodData?.period ?? null);

    const hoveredIndicatorOffsetTop = useMemo<number | null>(() => {
      if (!hoveredPeriodData) {
        return null;
      }
      const { mouseOffsetTop } = hoveredPeriodData;
      let offsetTop;
      if (shouldDisplayOverbookings) {
        offsetTop = shouldBarsOverlap ? OVERBOOKINGS_TOP : OVERBOOKINGS_TOP + ROW_PADDING;
      } else {
        offsetTop = shouldBarsOverlap ? 0 : ROW_PADDING;
      }
      if (mouseOffsetTop <= 0 || mouseOffsetTop >= dimensions.height) {
        return null;
      }
      const singleBarHeight = BAR_HEIGHT + VERTICAL_BARS_MARGIN;
      const barIndex = Math.max(
        0,
        Math.min(barRowsCount - 1, Math.floor((mouseOffsetTop - offsetTop) / singleBarHeight))
      );
      return offsetTop + barIndex * singleBarHeight;
    }, [hoveredPeriodData, shouldDisplayOverbookings, dimensions, barRowsCount, shouldBarsOverlap]);

    return (
      <>
        {shouldDisplayOverbookings && <Overbookings row={row} startDate={startDate} />}
        {shouldHoverPeriod && !isNil(hoveredIndicatorOffsetTop) && (
          <HoveredPeriodIndicator
            id={`${row.key}_hover`}
            calendarGranularity={calendarGranularity}
            period={hoveredPeriodData!.period}
            offsetTop={hoveredIndicatorOffsetTop}
            profileId={profileId!}
            activityId={activityId}
            center={shouldBarsOverlap}
          />
        )}
        <BasicRow
          row={row}
          rowRef={rowRef as RefObject<HTMLDivElement>}
          startDate={startDate}
          endDate={endDate}
          onLoadingChange={onLoadingChange}
          draggedBookingId={draggedBookingId}
          onBarDragStart={onBarDragStart}
          onBarDragEnd={onBarDragEnd}
          shouldBarsOverlap={shouldBarsOverlap}
          offsetTop={shouldDisplayOverbookings ? OVERBOOKINGS_TOP : 0}
        />
        {shouldDisplayMisalignments && (
          <Misalignments row={row} startDate={startDate} withPadding={!shouldBarsOverlap} />
        )}
      </>
    );
  }
);

BookingsRow.displayName = "BookingsRow";
